/** Creator subscription trial helpers (mirrors boxofix creatorSubscriptionTrialService). */

export const DEFAULT_CREATOR_TRIAL_DAYS = 14;

export type CreatorPlanTrial = {
    available?: boolean;
    days?: number;
    already_used?: boolean;
    reason?: string | null;
    cta_label?: string | null;
    tokenize_amount_kobo?: number;
    tokenize_currency?: string;
    /** From GlobalConfiguration features.creator_subscription_trial */
    feature_enabled?: boolean;
};

/** Optional user markers when present on profile / admin payloads. */
export type CreatorTrialUserMarkers = {
    creator_trial?: {
        used?: boolean;
        used_at?: string | Date | null;
        subscription_id?: string | null;
    } | null;
};

function asCreatorTrialUserMarkers(user?: unknown): CreatorTrialUserMarkers | null {
    if (!user || typeof user !== 'object') return null;
    return user as CreatorTrialUserMarkers;
}

export function resolveCreatorTrialDays(trial?: CreatorPlanTrial | null): number {
    const days = Number(trial?.days);
    return Number.isFinite(days) && days > 0 ? days : DEFAULT_CREATOR_TRIAL_DAYS;
}

/** Prefer backend `cta_label`, else build from days. */
export function resolveCreatorTrialCtaLabel(trial?: CreatorPlanTrial | null): string {
    const label = trial?.cta_label?.trim();
    if (label) return label;
    return `Start ${resolveCreatorTrialDays(trial)} day Trial`;
}

/** True when the user has already consumed their one lifetime creator trial. */
export function hasUsedCreatorTrialMarkers(user?: unknown): boolean {
    const trial = asCreatorTrialUserMarkers(user)?.creator_trial;
    if (!trial) return false;
    if (trial.used === true) return true;
    if (trial.used_at) return true;
    if (trial.subscription_id) return true;
    return false;
}

/**
 * Whether the UI may offer / request a free trial.
 * Requires explicit `available: true` and rejects already-used / disabled markers.
 */
export function isCreatorTrialAvailable(trial?: CreatorPlanTrial | null, user?: unknown): boolean {
    if (!trial) return false;
    if (hasUsedCreatorTrialMarkers(user)) return false;
    if (trial.feature_enabled === false) return false;
    if (trial.already_used === true) return false;
    if (trial.reason === 'trial_already_used') return false;
    return trial.available === true;
}

/**
 * Whether checkout / subscribe payloads should include `start_trial: true`.
 * Never request a trial when upgrading an existing manageable subscription.
 */
export function shouldRequestCreatorTrial(
    trial?: CreatorPlanTrial | null,
    opts?: { user?: unknown; hasManageableSubscription?: boolean },
): boolean {
    if (opts?.hasManageableSubscription) return false;
    return isCreatorTrialAvailable(trial, opts?.user);
}

/** First trial payload from the user's plan or the catalog. */
export function pickCreatorTrial(
    subscription?: { trial?: CreatorPlanTrial | null } | null,
    plans?: Array<{ trial?: CreatorPlanTrial | null }> | null,
): CreatorPlanTrial | null {
    if (subscription?.trial) return subscription.trial;
    const withTrial = plans?.filter(p => p?.trial) ?? [];
    // Prefer a definitive "used / unavailable" annotation over a stale available:true.
    const locked = withTrial.find(
        p => p.trial?.already_used === true || p.trial?.reason === 'trial_already_used' || p.trial?.available === false,
    )?.trial;
    if (locked) return locked;
    return withTrial[0]?.trial ?? null;
}

/** Apply-button copy when a premium campaign is gated and the creator can start a trial. */
export function resolveCreatorTrialApplyCtaLabel(trial?: CreatorPlanTrial | null): string {
    const days = resolveCreatorTrialDays(trial);
    return `Try Pro free for ${days} days`;
}

export function formatCreatorTrialActiveToast(trialDays?: number | null): string {
    const days =
        trialDays != null && Number.isFinite(Number(trialDays)) && Number(trialDays) > 0
            ? Number(trialDays)
            : DEFAULT_CREATOR_TRIAL_DAYS;
    return `Your ${days}-day trial is active!`;
}

export function formatCreatorTrialStartedToast(trialDays?: number | null): string {
    const days =
        trialDays != null && Number.isFinite(Number(trialDays)) && Number(trialDays) > 0
            ? Number(trialDays)
            : DEFAULT_CREATOR_TRIAL_DAYS;
    return `Your ${days}-day trial has started!`;
}
